import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../../services/clinical-onboarding.service';
import { ToastService } from '../../../services/general/toast.service';
import {
  ClinicianDashboardKpis,
  ClinicianDashboardRow,
  ClinicianMacroStatus,
  clinicalOnboardingI18nKeySuffix,
} from '../../../models/clinical-onboarding.model';
import {
  ClinicalOnboardingClinicianDetailDialogComponent
} from '../clinical-onboarding-clinician-detail-dialog/clinical-onboarding-clinician-detail-dialog.component';
import {
  ModalRegisterClinicalOnboardingCandidatesComponent
} from '../../../modals/modal-register-clinical-onboarding-candidates/modal-register-clinical-onboarding-candidates.component';
import {
  ModalImportClinicalOnboardingCandidatesComponent
} from '../../../modals/modal-import-clinical-onboarding-candidates/modal-import-clinical-onboarding-candidates.component';

/**
 * DBEK-407: dashboard di avanzamento onboarding clinico (KPI + Tabella-1, piano §4) -
 * lato gestore, mostrata come nuova tab in survey-details-page. Colonne di
 * macro-stato (DBEK-409) e semaforo di verifica (DBEK-408) si aggiungono sopra
 * questa base in ticket successivi, stesso schema di dipendenza gia' seguito lato
 * backend (branch-per-ticket con rebase quando serve davvero).
 */
@Component({
  selector: 'app-clinical-onboarding-dashboard',
  templateUrl: './clinical-onboarding-dashboard.component.html',
  styleUrls: ['./clinical-onboarding-dashboard.component.scss'],
  standalone: false,
})
export class ClinicalOnboardingDashboardComponent implements OnChanges {
  @Input() surveyId: string | number;

  loading = false;
  kpis: ClinicianDashboardKpis | null = null;
  dataSource = new MatTableDataSource<ClinicianDashboardRow>([]);

  // Colonne sempre visibili (identita', stato, azioni) + colonne opzionali
  // nascondibili dal menu "Colonne" - stesso identico pattern di
  // survey-team.component.ts (columnSettings/toggleColumn/rebuildColumns), che
  // risolve lo stesso problema (tabella con troppe colonne per stare comoda a
  // schermo, qui 7 colonne dati oltre a identita'/stato/azioni).
  private readonly mandatoryColumns = ['select', 'name', 'macro_status'];
  displayedColumns: string[] = [];
  columnSettings: { key: string; label: string; visible: boolean }[] = [
    { key: 'verification_ok', label: 'components.clinical-onboarding.dashboard.column-verification', visible: true },
    { key: 'fiscal_regime', label: 'components.clinical-onboarding.wizard.field-fiscal-regime', visible: true },
    { key: 'net_amount_indicative', label: 'components.clinical-onboarding.dashboard.column-net-amount', visible: true },
    { key: 'documents_received_at', label: 'components.clinical-onboarding.dashboard.column-documents-received-at', visible: false },
    { key: 'pa_authorization_status', label: 'components.clinical-onboarding.dashboard.column-pa-authorization', visible: false },
    { key: 'manual_participation_override', label: 'components.clinical-onboarding.dashboard.column-override', visible: false },
    { key: 'pa_communication_status', label: 'components.clinical-onboarding.dashboard.column-pa-communication', visible: false },
  ];

  // 'ALL' come sentinella, non null: stesso identico pattern gia' collaudato in
  // survey-team.component.ts (filters.responseStatus) - un mat-option con
  // [value]="null" non mostra il testo selezionato nel trigger del mat-select
  // (bug osservato: il campo restava vuoto invece di leggere "Tutti gli stati").
  statusFilter: string = 'ALL';
  readonly ClinicianMacroStatus = ClinicianMacroStatus;

  // Sollecito manuale ammesso solo per chi deve ancora completare il pacchetto -
  // stesso identico gate lato backend (ClinicalOnboardingController::remindCandidates):
  // il testo della notifica ("pacchetto ancora incompleto o non firmato") sarebbe
  // fuorviante per un candidato gia' SIGNED/oltre, o EXPIRED/EXCLUDED.
  private readonly REMINDABLE_STATUSES: string[] = [ClinicianMacroStatus.WAITING, ClinicianMacroStatus.IN_PROGRESS];
  selection = new SelectionModel<ClinicianDashboardRow>(true, []);
  sendingReminder = false;

  constructor(
    private clinicalOnboardingService: ClinicalOnboardingService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    this.rebuildColumns();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.surveyId && this.surveyId) {
      this.load();
    }
  }

  private rebuildColumns(): void {
    const visibleOptionalKeys = this.columnSettings.filter((s) => s.visible).map((s) => s.key);
    this.displayedColumns = [...this.mandatoryColumns, ...visibleOptionalKeys, 'actions'];
  }

  toggleColumn(setting: { key: string; label: string; visible: boolean }): void {
    setting.visible = !setting.visible;
    this.rebuildColumns();
  }

  load(): void {
    if (!this.surveyId) {
      return;
    }
    this.loading = true;
    this.clinicalOnboardingService.getDashboard(
      this.surveyId,
      this.statusFilter === 'ALL' ? null : this.statusFilter,
      (data) => {
        this.loading = false;
        this.kpis = data.kpis;
        this.dataSource.data = data.clinicians || [];
        this.selection.clear();
      },
      () => {
        this.loading = false;
      },
    );
  }

  isRemindable(row: ClinicianDashboardRow): boolean {
    return this.REMINDABLE_STATUSES.includes(row.macro_status);
  }

  get remindableRows(): ClinicianDashboardRow[] {
    return this.dataSource.data.filter((row) => this.isRemindable(row));
  }

  get isAllRemindableSelected(): boolean {
    const remindable = this.remindableRows;
    return remindable.length > 0 && remindable.every((row) => this.selection.isSelected(row));
  }

  toggleSelectAllRemindable(): void {
    if (this.isAllRemindableSelected) {
      this.selection.clear();
    } else {
      this.selection.select(...this.remindableRows);
    }
  }

  /** Sollecito per la selezione multipla corrente. */
  sendBulkReminder(): void {
    const candidateIds = this.selection.selected.map((row) => row.candidate_id);
    this.sendReminder(candidateIds);
  }

  /** Sollecito per un singolo candidato, dal bottone in tabella - non richiede selezione. */
  sendSingleReminder(row: ClinicianDashboardRow): void {
    this.sendReminder([row.candidate_id]);
  }

  private sendReminder(candidateIds: number[]): void {
    if (candidateIds.length === 0 || this.sendingReminder) {
      return;
    }
    this.sendingReminder = true;
    this.clinicalOnboardingService.remindCandidates(
      this.surveyId,
      candidateIds,
      (result) => {
        this.sendingReminder = false;
        this.toastService.success(
          this.translateService.instant('generic.success'),
          this.translateService.instant('components.clinical-onboarding.dashboard.reminder-sent', { count: result.sent }),
        );
        this.selection.clear();
      },
      () => {
        this.sendingReminder = false;
      },
    );
  }

  onStatusFilterChange(value: string): void {
    this.statusFilter = value || 'ALL';
    this.load();
  }

  /**
   * DBEK-392 (ridisegno "candidato"): candidateId, non team_member_id - quest'ultimo
   * e' null finche' il candidato non e' stato importato nel vero team member della
   * survey (piano §"registrare i futuri medici prima che la survey sia partita"), ma
   * il dettaglio deve restare consultabile anche prima, dato che
   * ClinicalOnboardingController::getClinicianDetail risolve sempre a partire dal
   * Candidate. panelClass 'medium-dialog' invece di width/maxHeight inline (audit
   * UI/UX), stessa chrome standard di modal-add-project. 'clinician-detail-dialog-panel'
   * aggiunto in piu' (feedback utente: "stessa dimensione [...] per tutti i tab") -
   * fissa larghezza/altezza del dialog stesso in _common.scss, cosi' passare da
   * "Dettaglio" (contenuto lungo) a "Storico attivita'" (spesso piu' corto) non fa
   * piu' saltare le dimensioni del dialog - 'medium-dialog' da solo lascia il dialog
   * auto-dimensionato sul contenuto del tab corrente.
   */
  openClinicianDetail(row: ClinicianDashboardRow): void {
    const dialogRef = this.dialog.open(ClinicalOnboardingClinicianDetailDialogComponent, {
      data: { surveyId: this.surveyId, candidateId: row.candidate_id },
      panelClass: ['medium-dialog', 'clinician-detail-dialog-panel'],
    });
    // Un'azione sul documento nel dialog puo' far cambiare macro_status/verifica -
    // ricarico la Tabella-1 alla chiusura cosi' resta coerente senza duplicare la
    // logica di ricalcolo, che vive gia' server-side.
    dialogRef.afterClosed().subscribe(() => this.load());
  }

  i18nKeySuffix(value: string): string {
    return clinicalOnboardingI18nKeySuffix(value);
  }

  /**
   * Bug: la colonna "Verifica" mostrava sempre la spunta verde/rossa in base al
   * solo verification_ok, che pero' vale true anche quando l'IBAN non e' ancora
   * stato inserito (nessun problema rilevato != verifica realmente superata).
   * Stessa logica a 3 stati gia' usata in ClinicalOnboardingClinicianDetailDialogComponent.verificationDisplay:
   * un override esplicito del gestore vince sempre, altrimenti "nessun IBAN"
   * mostra uno stato neutro invece di un falso positivo verde.
   */
  verificationDisplayFor(row: ClinicianDashboardRow): { icon: string; tooltipKey: string; statusClass: 'ok' | 'ko' | 'neutral' } {
    if (row.verification_override !== null) {
      return row.verification_override
        ? { icon: 'check_circle', tooltipKey: 'components.clinical-onboarding.clinician-detail.verification-override-valid', statusClass: 'ok' }
        : { icon: 'cancel', tooltipKey: 'components.clinical-onboarding.clinician-detail.verification-override-invalid', statusClass: 'ko' };
    }

    if (!row.verification_ok) {
      return { icon: 'cancel', tooltipKey: 'components.clinical-onboarding.dashboard.verification-ko', statusClass: 'ko' };
    }

    if (!row.has_iban) {
      return { icon: 'info', tooltipKey: 'components.clinical-onboarding.clinician-detail.verification-no-iban-yet', statusClass: 'neutral' };
    }

    return { icon: 'check_circle', tooltipKey: 'components.clinical-onboarding.dashboard.verification-ok', statusClass: 'ok' };
  }

  /**
   * DBEK-395 (ridisegno "candidato"): disponibile fin da qui, che la survey sia
   * ancora in bozza o gia' avviata (piano §"registrare i futuri medici prima che la
   * survey sia effettivamente partita") - a differenza di importClinicianDetail,
   * questa azione non richiede che il candidato esista gia'.
   */
  openRegisterCandidates(): void {
    const dialogRef = this.dialog.open(ModalRegisterClinicalOnboardingCandidatesComponent, {
      data: { surveyId: this.surveyId },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.load();
      }
    });
  }

  /**
   * DBEK-395: importazione MANUALE, in aggiunta al trigger automatico che scatta
   * quando la survey esce dalla bozza (vedi OrganizationSurvey::booted() lato
   * backend) - per chi vuole importare in anticipo un sottoinsieme gia' pronto.
   */
  openImportCandidates(): void {
    const dialogRef = this.dialog.open(ModalImportClinicalOnboardingCandidatesComponent, {
      data: { surveyId: this.surveyId },
      panelClass: 'medium-dialog',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.load();
      }
    });
  }
}
